import {useEffect,useMemo,useRef} from 'react';
import {courseApi,courseFileUrl} from '../course-api';
import {useCoursePlayback} from '../hooks/useCoursePlayback';
import {courseProgressReporter,stopReaderVideo,validCourseVideoBinding,type CourseVideoBinding} from '../reader-media';
import {Reader,type ReaderProps} from './Reader';

export interface CourseReaderProps extends ReaderProps {courseVideo?:CourseVideoBinding}

/** The existing Reader keeps rendering, references, selections and controls. */
export function CourseReader({courseVideo,...props}:CourseReaderProps){
  if(props.file?.storage!=='course')return <Reader {...props}/>;
  if(props.file.kind!=='video')return <Reader {...props} readOnlySource/>;
  if(!validCourseVideoBinding(props.file,courseVideo))return <Reader {...props} readOnlySource videoTransport={{initialPosition:0,error:'无法识别课程视频，请从课程目录重新打开。'}}/>;
  return <CourseVideoReader key={`${props.userId}:${props.file.id}`} {...props} binding={courseVideo}/>;
}

function CourseVideoReader({binding,...props}:ReaderProps&{binding:CourseVideoBinding}){
  const visible=props.visible!==false&&props.playbackActive!==false;
  const element=useRef<HTMLVideoElement|null>(null),playAfterRetry=useRef(false);
  const progress=useMemo(()=>courseProgressReporter(binding,value=>courseApi.progress(binding.courseId,binding.lessonId,value)),
    [binding.courseId,binding.lessonId,binding.videoFileId,props.userId]);
  const stop=()=>{
    const player=element.current;
    if(player&&player.readyState>0)progress.event('pause',{position:player.currentTime,duration:player.duration});
    void progress.suspend();stopReaderVideo(player);
  };
  const playback=useCoursePlayback(binding.courseId,visible?binding.lessonId:undefined,stop);
  useEffect(()=>{
    if(!visible)stop();
    const hide=()=>{if(document.visibilityState==='hidden')void progress.flush();};
    const unload=()=>{void progress.flush();};
    document.addEventListener('visibilitychange',hide);window.addEventListener('pagehide',unload);
    return()=>{document.removeEventListener('visibilitychange',hide);window.removeEventListener('pagehide',unload);stop();};
  },[visible,progress]);
  return <Reader {...props} readOnlySource videoTransport={{
    url:visible?playback.url:undefined,
    initialPosition:progress.position,
    subtitleUrl:binding.subtitle?courseFileUrl(binding.subtitle):undefined,
    elementRef:element,busy:visible&&playback.busy,error:visible?playback.error:undefined,
    onRetry:visible?()=>{playAfterRetry.current=true;stop();void playback.retry();}:undefined,
    onEvent:(event,position)=>{if(visible)progress.event(event,position);},
    onReady:player=>{if(visible&&playAfterRetry.current){playAfterRetry.current=false;void player.play().catch(()=>{});}},
  }}/>;
}
